import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { Collapse, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import classnames from 'classnames';
import { format } from 'date-fns';
import React from 'react';
import { RecurringTasksListView, TaskStatusEnum, TaskSummaryType } from '@redux/tasks/tasksTypes';
import { CardsIcon } from 'components/Icons';
import { StatusLabel } from 'components/StatusLabel/StatusLabel';
import { TaskTypeIcon } from 'components/TaskTypeIcon';
import { TasksTableColName } from 'containers/Tasks/components/TasksTable/TasksTable.types';
import { TasksTableCell } from 'containers/Tasks/components/TasksTableCell/TasksTableCell';
import { TASK_LABEL_STATUSES } from 'containers/Tasks/Tasks.const';
import { Color } from 'utils/color';
import { getDateFromStringIgnoreTimezone } from 'utils/time';
import style from './PatientTask.scss';

interface PatientTaskProps {
  task: TaskSummaryType;
  onSelectTaskSwitch: (taskId: string) => void;
  onCancelTask: (taskId: string, isSingleCancel: boolean) => void;
  isExpanded: boolean;
  childTasks: RecurringTasksListView[];
  onToggleExpand: () => void;
}

const ProgressBar: React.FC<{ progress: number; completed: number; total: number }> = ({ progress, completed, total }) => (
  <div className={style.progressBarContainer}>
    <div className={style.progressBarWrapper}>
      <div className={style.progressBar} style={{ width: `${progress * 100}%`, backgroundColor: Color.DARK_GREEN }} />
    </div>
    <span className={style.progressText} data-testid="progress-text">{`${completed}/${total}`}</span>
  </div>
);

export const PatientTask: React.FC<PatientTaskProps> = ({ task, onSelectTaskSwitch, onCancelTask, isExpanded, childTasks, onToggleExpand }) => {
  const { taskName, taskSummary, taskType, numberOfTasksCompleted, numberOfTasksTotal, scheduledForDateTime, taskStatus } = task;

  const onSelect = () => {
    onSelectTaskSwitch(task.recurringTasksList?.[0]?.taskId || task.taskId);
  };

  const onCancel = (isSingleCancel: boolean) => {
    onCancelTask(task.recurringTasksList?.[0]?.taskId || task.taskId, isSingleCancel);
  };

  const canExpand = (task.recurringTasksList?.length || 0) > 1;

  const progress = (numberOfTasksTotal || 0) > 0 ? (numberOfTasksCompleted || 0) / (numberOfTasksTotal || 0) : 0;

  const getStatusComponent = () => {
    if ((task.recurringTasksList?.length || 0) <= 1) {
      return <TasksTableCell colName={TasksTableColName.STATUS} value={task.recurringTasksList?.[0]?.taskStatus || task.taskStatus} />;
    }
    return null;
  };

  return (
    <React.Fragment>
      <TableRow className={style.parentTaskRow} onClick={canExpand ? onToggleExpand : onSelect} style={{ cursor: 'pointer' }} data-testid={`task-row-${taskName}`}>
        <TableCell className={classnames(style.tableCell, style.nameColumn)}>
          <div className={style.nameContainer}>
            <div className={style.expansionWrapper}>{canExpand && (isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />)}</div>
            <TaskTypeIcon taskType={taskType} />
            <div className={style.infoWrapper}>
              <span>{taskSummary || taskName}</span>
              <div className={style.taskName}>
                {taskType}
                {task.engagementPlanName && (
                  <>
                    <span className={style.dotSeparator}>·</span>
                    <CardsIcon color={Color.BLACK} />
                    <span className={style.engagementPlanName} data-testid={`engagement-plan-name-${taskName}`}>
                      {task.engagementPlanName}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </TableCell>
        <TableCell className={classnames(style.tableCell, style.completedColumn)}>
          <div data-testid={`task-progress-bar-${taskName}`}>
            <ProgressBar progress={progress} completed={numberOfTasksCompleted || 0} total={numberOfTasksTotal || 0} />
          </div>
        </TableCell>
        <TableCell className={classnames(style.tableCell, style.dateColumn)} align="right">
          <div className={style.dateColumnContentWrapper}>
            <div data-testid={`task-status-${taskName}`} className={style.statusContainer}>
              {getStatusComponent()}
            </div>
            <TasksTableCell colName={TasksTableColName.SCHEDULED_DATE} value={scheduledForDateTime} />
          </div>
        </TableCell>
      </TableRow>
      {isExpanded && canExpand && (
        <TableRow>
          <TableCell className={style.childTaskRow} colSpan={4}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Table className={style.childTaskTable} sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classnames(style.tableCell, style.recurringTasksHeader)}>Recurring tasks</TableCell>
                    <TableCell className={classnames(style.tableCell, style.statusHeader)} align="right">
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {childTasks.map((childTask) => {
                    return (
                      <TableRow
                        hover
                        key={childTask.taskId}
                        className={style.clickableRow}
                        onClick={() => onSelectTaskSwitch(childTask.taskId)}
                        data-testid={`sub-task-row-${childTask.taskId}`}
                      >
                        <TableCell className={classnames(style.tableCell, style.recurringTaskCell)}>
                          <div>{childTask.scheduledForDateTime ? format(getDateFromStringIgnoreTimezone(childTask.scheduledForDateTime) || new Date(), 'MMM d, h:mm a') : ''}</div>
                          <div className={style.taskName}>{childTask.taskSummary || childTask.taskName}</div>
                        </TableCell>
                        <TableCell className={classnames(style.tableCell, style.statusCell)} sx={{ display: 'flex' }}>
                          <TasksTableCell colName={TasksTableColName.STATUS} value={childTask.taskStatus} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
};
